import { hapticFeedback } from './HapticFeedback';

/**
 * RippleEffect service provides methods to trigger visual ripple effects with haptic feedback
 * as an alternative to toast notifications.
 */
class RippleEffect {
  /**
   * Apply a ripple effect to an element with success feedback
   * @param {HTMLElement} element - The element to apply the ripple effect to
   * @param {Object} options - Configuration options
   */
  async success(element, options = {}) {
    try {
      // Apply visual ripple effect
      this.applyRippleEffect(element, { 
        color: 'rgba(40, 167, 69, 0.5)', // Success green color
        ...options 
      });
      
      // Trigger haptic feedback
      await hapticFeedback.success();
    } catch (error) {
      console.warn('Ripple effect failed:', error);
    }
  }

  /**
   * Apply a ripple effect to an element with error feedback
   * @param {HTMLElement} element - The element to apply the ripple effect to
   * @param {Object} options - Configuration options
   */
  async error(element, options = {}) {
    try {
      // Apply visual ripple effect
      this.applyRippleEffect(element, { 
        color: 'rgba(220, 53, 69, 0.5)', // Error red color
        ...options 
      });
      
      // Trigger haptic feedback
      await hapticFeedback.error();
    } catch (error) {
      console.warn('Ripple effect failed:', error);
    }
  }

  /**
   * Apply a ripple effect to an element with warning feedback
   * @param {HTMLElement} element - The element to apply the ripple effect to
   * @param {Object} options - Configuration options
   */
  async warning(element, options = {}) {
    try {
      // Apply visual ripple effect
      this.applyRippleEffect(element, { 
        color: 'rgba(255, 193, 7, 0.5)', // Warning yellow color
        ...options 
      });
      
      // Trigger haptic feedback
      await hapticFeedback.warning();
    } catch (error) {
      console.warn('Ripple effect failed:', error);
    }
  }

  /**
   * Apply a ripple effect to an element with info feedback
   * @param {HTMLElement} element - The element to apply the ripple effect to
   * @param {Object} options - Configuration options
   */
  async info(element, options = {}) {
    try {
      // Apply visual ripple effect
      this.applyRippleEffect(element, { 
        color: 'rgba(23, 162, 184, 0.5)', // Info blue color
        ...options 
      });
      
      // Trigger haptic feedback
      await hapticFeedback.medium();
    } catch (error) {
      console.warn('Ripple effect failed:', error);
    }
  }

  /**
   * Apply a ripple effect to an element
   * @param {HTMLElement} element - The element to apply the ripple effect to
   * @param {Object} options - Configuration options
   */
  applyRippleEffect(element, options = {}) {
    if (!element) {
      console.warn('No element provided for ripple effect');
      return;
    }

    // Set default options
    const config = {
      color: 'rgba(0, 0, 0, 0.2)',
      duration: 600,
      ...options
    };

    // Make sure the element has position relative for absolute positioning of the ripple
    const computedStyle = window.getComputedStyle(element);
    if (computedStyle.position === 'static') {
      element.style.position = 'relative';
    }

    // Create the ripple element
    const ripple = document.createElement('span');
    ripple.style.position = 'absolute';
    ripple.style.top = '50%';
    ripple.style.left = '50%';
    ripple.style.width = '5px';
    ripple.style.height = '5px';
    ripple.style.background = config.color;
    ripple.style.opacity = '0.5';
    ripple.style.borderRadius = '100%';
    ripple.style.transform = 'scale(1, 1) translate(-50%, -50%)';
    ripple.style.transformOrigin = '50% 50%';
    ripple.style.pointerEvents = 'none'; // Make sure it doesn't interfere with clicks

    // Add the ripple to the element
    element.appendChild(ripple);

    // Animate the ripple
    const animation = ripple.animate([
      {
        transform: 'scale(0, 0) translate(-50%, -50%)',
        opacity: 0.5
      },
      {
        transform: 'scale(20, 20) translate(-50%, -50%)',
        opacity: 0
      }
    ], {
      duration: config.duration,
      easing: 'ease-out',
      fill: 'forwards'
    });

    // Remove the ripple element when animation is complete
    animation.onfinish = () => {
      ripple.remove();
    };
  }
}

export const rippleEffect = new RippleEffect();